import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GooglePlacesService from '../services/googlePlaces';
import Icon from './shared/Icon';
import PoweredByGoogle from './shared/PoweredByGoogle';

/**
 * LocationAutocomplete - Autocomplete for city/destination selection
 * Uses Google Places API with (cities) filter for worldwide city coverage
 * Falls back to hardcoded popular cities when API is unavailable
 *
 * Selection callbacks — the distinction matters because recent-search and
 * popular-city suggestions carry NO place_id:
 * - `onSelect({ name, placeId, location })` fires synchronously on EVERY
 *   suggestion click, with whatever is known at that instant (`location` is
 *   set for GeoNames-backed suggestions, null otherwise). Use it when the
 *   pick itself should act (add a stop, create a chip) — waiting on
 *   onPlaceSelect drops recents/popular picks entirely.
 * - `onPlaceSelect({ name, location, placeId })` fires with coordinates:
 *   synchronously for GeoNames-backed suggestions, after the place-details
 *   round-trip for Google picks, never for recents/popular. Callers using
 *   both should treat it as a coordinate upgrade of the onSelect pick.
 */
function LocationAutocomplete({ value, onChange, onSelect, onPlaceSelect, onConfirm, placeholder, className }) {
  const { t } = useTranslation('search');
  const resolvedPlaceholder = placeholder ?? t('locationAutocomplete.placeholder');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimer = useRef(null);

  // Get recent searches from localStorage
  const getRecentSearches = () => {
    try {
      const recent = localStorage.getItem('recentLocationSearches');
      return recent ? JSON.parse(recent) : [];
    } catch (error) {
      return [];
    }
  };

  // Save recent search to localStorage
  const saveRecentSearch = (location) => {
    try {
      const recent = getRecentSearches();
      const updated = [location, ...recent.filter(item => item !== location)].slice(0, 5);
      localStorage.setItem('recentLocationSearches', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving recent search:', error);
    }
  };

  // Predefined popular cities for instant suggestions (fallback)
  const popularCities = [
    'New York, NY, USA',
    'Los Angeles, CA, USA',
    'San Francisco, CA, USA',
    'Chicago, IL, USA',
    'Miami, FL, USA',
    'Las Vegas, NV, USA',
    'Seattle, WA, USA',
    'Boston, MA, USA',
    'London, UK',
    'Paris, France',
    'Rome, Italy',
    'Barcelona, Spain',
    'Amsterdam, Netherlands',
    'Tokyo, Japan',
    'Sydney, Australia'
  ];

  // Filter popular cities based on input (fallback when API unavailable)
  const getFilteredPopularCities = (input) => {
    if (!input) {
      const recent = getRecentSearches();
      const combined = [...recent, ...popularCities];
      return Array.from(new Set(combined)).slice(0, 6);
    }

    const filtered = popularCities.filter(city =>
      city.toLowerCase().includes(input.toLowerCase())
    );

    return Array.from(new Set(filtered)).slice(0, 4);
  };

  // Search for cities using Google Places API
  const searchCities = async (input) => {
    if (!input || input.length < 2) {
      setSuggestions(getFilteredPopularCities(input).map(city => ({
        description: city,
        isLocal: true
      })));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Show local matches instantly while API loads
      const localMatches = getFilteredPopularCities(input).map(city => ({
        description: city,
        isLocal: true
      }));
      setSuggestions(localMatches);

      // Call Google Places API for city autocomplete
      const apiSuggestions = await GooglePlacesService.getCityAutocompleteSuggestions(input);

      if (apiSuggestions && apiSuggestions.length > 0) {
        // Combine API results with recent searches that match
        const recentMatches = getRecentSearches()
          .filter(city => city.toLowerCase().includes(input.toLowerCase()))
          .map(city => ({ description: city, isRecent: true }));

        // Merge: recent first, then API results (deduplicated)
        const apiFormatted = apiSuggestions.map(s => ({
          description: s.description,
          place_id: s.place_id,
          structured_formatting: s.structured_formatting,
          types: s.types,
          // GeoNames-backed suggestions carry coordinates directly — a pick
          // delivers them synchronously, no place-details fetch.
          latitude: s.latitude,
          longitude: s.longitude,
          isApi: true
        }));

        // Deduplicate by description
        const seen = new Set();
        const merged = [...recentMatches, ...apiFormatted].filter(s => {
          const key = s.description.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setSuggestions(merged.slice(0, 8));
      } else {
        // Keep local matches if API returned nothing
        setSuggestions(localMatches);
      }
    } catch (error) {
      console.error('Error searching cities:', error);
      // Fall back to local matches on error
      setSuggestions(getFilteredPopularCities(input).map(city => ({
        description: city,
        isLocal: true
      })));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(true);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      searchCities(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionClick = async (suggestion) => {
    const selectedText = suggestion.description;
    onChange(selectedText);
    setShowSuggestions(false);
    setSuggestions([]);
    saveRecentSearch(selectedText);

    // Coordinates already on the suggestion (GeoNames-backed local lookup).
    const knownLocation = Number.isFinite(suggestion.latitude) && Number.isFinite(suggestion.longitude)
      ? { lat: suggestion.latitude, lng: suggestion.longitude }
      : null;

    // Every pick fires onSelect immediately — recents/popular suggestions
    // have no place_id, and API picks shouldn't wait on the details fetch.
    if (onSelect) {
      onSelect({ name: selectedText, placeId: suggestion.place_id || null, location: knownLocation });
    }

    // Known coordinates — deliver synchronously, no details round-trip.
    if (onPlaceSelect && knownLocation) {
      onPlaceSelect({
        name: selectedText,
        location: knownLocation,
        placeId: suggestion.place_id || null
      });
      return;
    }

    // Neither coordinates nor a place_id — the recents/popular list shown on
    // focus, before any query has run. Resolve locally (free) so the pick
    // still lands with coordinates; an ambiguous name resolves to null and
    // the server-side save backstop takes it from there.
    if (onPlaceSelect && !suggestion.place_id) {
      const coords = await GooglePlacesService.lookupCityCoords(selectedText);
      if (coords) {
        onPlaceSelect({ name: selectedText, location: coords, placeId: null });
      }
      return;
    }

    // If this is from Google API and we have a place_id, get coordinates
    if (suggestion.place_id && onPlaceSelect) {
      try {
        const placeDetails = await GooglePlacesService.getPlaceDetails(suggestion.place_id);
        // /api/places/details returns location nested under geometry; some
        // callers/cached shapes still expose a top-level `location`. Read both.
        const location = placeDetails?.geometry?.location || placeDetails?.location || null;
        if (location) {
          onPlaceSelect({
            name: selectedText,
            location, // { lat, lng }
            placeId: suggestion.place_id
          });
        } else {
          // place_id is still useful even without coords — pass it through so
          // the backend can resolve country_code from google_place_cache.
          onPlaceSelect({
            name: selectedText,
            location: null,
            placeId: suggestion.place_id
          });
        }
      } catch (error) {
        console.error('Error getting place details:', error);
      }
    }
  };

  // Handle input focus
  const handleFocus = () => {
    setShowSuggestions(true);
    if (suggestions.length === 0) {
      setSuggestions(getFilteredPopularCities(value).map(city => ({
        description: city,
        isLocal: true
      })));
    }
  };

  // Handle input blur (with delay to allow clicks)
  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={resolvedPlaceholder}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          // Escape dismisses an open suggestion list and goes no further, so
          // a dialog this field sits in (the Trip setup sheet) doesn't close
          // with it. With no list open, Escape passes through as usual.
          if (e.key === 'Escape' && showSuggestions && suggestions.length > 0) {
            e.stopPropagation();
            setShowSuggestions(false);
            return;
          }
          if (e.key === 'Enter' && onConfirm) {
            e.preventDefault();
            setShowSuggestions(false);
            onConfirm();
          }
        }}
        // The text colour is always ours: an input doesn't inherit one from
        // the page, so in dark mode a caller's bg-surface showed black text.
        className={`text-content-primary placeholder:text-content-muted ${className || "w-full p-3 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"}`}
      />

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-surface border border-border rounded-lg shadow-elevation-2 z-modal mt-1 max-h-60 overflow-y-auto">
          {isLoading && (
            <div className="px-4 py-2 text-content-secondary text-sm">
              <div className="inline-block w-3 h-3 border-2 border-primary/20 border-t-primary rounded-full animate-spin mr-2"></div>
              {t('locationAutocomplete.searchingLabel')}
            </div>
          )}

          {suggestions.map((suggestion, index) => {
            const mainText = suggestion.structured_formatting?.main_text || suggestion.description;
            const secondaryText = suggestion.structured_formatting?.secondary_text || '';
            const isRecent = suggestion.isRecent || getRecentSearches().includes(suggestion.description);

            return (
              <button
                key={suggestion.place_id || index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left px-4 py-3 border-b border-border/50 last:border-b-0 hover:bg-background focus-visible:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-light transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-content-muted mr-3">
                    <Icon name={isRecent ? 'clock' : 'mapPin'} className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-content-primary">{mainText}</span>
                    {secondaryText && (
                      <span className="text-content-secondary text-sm ml-1">
                        {secondaryText}
                      </span>
                    )}
                  </div>
                  {isRecent && (
                    <span className="ml-auto text-xs text-content-muted">{t('locationAutocomplete.recentBadge')}</span>
                  )}
                </div>
              </button>
            );
          })}

          {!isLoading && suggestions.length === 0 && value && (
            <div className="px-4 py-3 text-content-secondary text-sm">
              {t('locationAutocomplete.noResults')}
            </div>
          )}

          {/* Attribution only when Google predictions are showing — GeoNames
              results (place_id: null), recents and popular cities aren't Google's. */}
          {suggestions.some(s => s.isApi && s.place_id) && (
            <PoweredByGoogle variant="text" align="end" className="sticky bottom-0 bg-surface border-t border-border" />
          )}
        </div>
      )}
    </div>
  );
}

export default LocationAutocomplete;
